/**
 * ngArsoReport — shared, pure render core for the Nova Gorica Kakovost zraka
 * (ARSO/EEA/WebMet/EnCLOD) report.
 *
 * Extracted from NgArsoPanel so the sidebar panel AND the standalone
 * `/ng-report` page render byte-identical chart + table + banner from the same
 * `GET …/arso/timeseries` response. Pure functions only (data → HTML / option);
 * the ECharts instance lifecycle stays with each caller.
 *
 * Issue #359 (open report in a new tab). The panel and the page both import from
 * here — no second copy of this logic.
 *
 * #720: what is drawn is `arsoDisplaySeries(response)` — the shared list that
 * names every series (parameter names, and the station's name when two
 * stations are compared). This module computes nothing: it lays that list out.
 */

import type { EChartsOption } from 'echarts';
import type {
  ArsoParameter,
  ArsoSeries,
  ArsoSeriesParameter,
  ArsoStationMatched,
  TimeseriesResponse,
} from '../../../../../../shared/types/ngArso.js';
import { formatLjubljanaTs } from '../../../../../../shared/utils/ljubljanaTime.js';
import {
  arsoDisplaySeries,
  arsoSeriesLabel,
  arsoStationLabel,
} from '../../../../../../shared/utils/ngArsoLabels.js';
import {
  ljubljanaAxisLabel,
  ljubljanaAxisPointerLabel,
  ljubljanaTooltipFormatter,
} from '../ngChartTime.js';
import { legendName, sensorRangeLabel } from './ngPrometReport.js';

export type ArsoInterval = 'hourly' | 'daily';

/** A series as the chart takes it; `second` marks the comparison station's line. */
export type ArsoChartSeries = ArsoSeries & { second?: boolean };

/**
 * Parameters ticked when the panel opens a station and the user has not chosen
 * any (#717, #720): a weather station shows weather; an air-quality-only
 * station shows PM10 and ozone (it measures no temperature — Otlica holds
 * ozone alone); every other station keeps the temperature + PM10 default.
 * One request per series; empty series are hidden.
 */
export function defaultArsoParameters(kind: ArsoStationMatched['kind']): ArsoParameter[] {
  if (kind === 'meteo') return ['t', 'wind_speed', 'padavine'];
  if (kind === 'aq') return ['pm10', 'o3'];
  return ['t', 'pm10'];
}

// Render-specific Slovenian copy shared by panel + report page.
export const ARSO_COPY = {
  empty: 'V izbranem obdobju ni podatkov.',
  // A point without a station (#717): say why, so it never reads as a fault.
  outOfPilotBanner: 'za to merilno mesto ni podatkov',
  outOfPilotHintTitle: 'Za to merilno mesto ni podatkov',
  outOfPilotHintBody:
    'Na javni nadzorni plošči projekta EnCLOD ni naprave, ki bi jo lahko povezali s tem merilnim mestom, zato za to mesto ne prevzemamo meritev.',
  disclosureTitle: 'Pojasnila',
  chartTitle: 'Časovna vrsta',
  chartEmpty: 'Ni točk za prikaz v izbranem obdobju.',
  tableTitle: 'Vrednosti',
  tableTime: 'Čas',
  // Joins the two station names of a comparison in the report page's subtitle.
  compareJoin: 'in',
  // The key under the chart of a comparison: which line style is which station.
  compareKeySolid: 'Polna črta',
  compareKeyDashed: 'Črtkana črta',
};

export interface ArsoBannerState {
  visible: boolean;
  outOfPilot: boolean;
  html: string;
}

/**
 * Compute the disclosure / out-of-pilot banner content. The caller toggles the
 * `is-visible` / `is-out-of-pilot` classes on its own banner element and sets
 * innerHTML — keeping the banner content identical between panel and page.
 * With a comparison each station gets its own list (their sources differ).
 */
export function buildArsoBanner(data: TimeseriesResponse): ArsoBannerState {
  if (data.station.out_of_pilot) {
    return {
      visible: true,
      outOfPilot: true,
      html: `<strong>»${escapeHtml(arsoStationLabel(data.station))}«: ${escapeHtml(ARSO_COPY.outOfPilotBanner)}.</strong>`,
    };
  }
  const blocks = [
    { name: data.station.name, notes: data.disclosures },
    ...(data.compare ? [{ name: data.compare.station.name, notes: data.compare.disclosures }] : []),
  ].filter((b) => b.notes.length > 0);
  if (blocks.length > 0) {
    return {
      visible: true,
      outOfPilot: false,
      html: blocks
        .map(
          (b) =>
            `<strong>${escapeHtml(b.name)} — ${ARSO_COPY.disclosureTitle}:</strong>` +
            `<ul class="ngarso-banner-list">${b.notes.map((d) => `<li>${escapeHtml(d)}</li>`).join('')}</ul>`,
        )
        .join(''),
    };
  }
  return { visible: false, outOfPilot: false, html: '' };
}

/**
 * The report page's subtitle (plain text — the caller escapes it): the station,
 * the second station of a comparison, and the range day-first with its last
 * day inclusive (»1. 7. 2026 – 7. 7. 2026«). Never an internal id.
 */
export function arsoReportSubtitle(data: TimeseriesResponse): string {
  const first = arsoStationLabel(data.station);
  const second = !data.station.out_of_pilot && data.compare ? data.compare.station.name : null;
  const stations = second ? `${first} ${ARSO_COPY.compareJoin} ${second}` : first;
  return `${stations} · ${sensorRangeLabel(data.range)}`;
}

/** What the SHP export can do with the stations of an answer (#736). */
export interface ArsoShpNote {
  /** True = no station of the answer can be put on a map: the button is off. */
  blocked: boolean;
  /** The reason the button is off, or which station will not be in the layer; '' = nothing to say. */
  note: string;
}

const ARSO_SHP_NO_LOCATION = 'Izvoz SHP ni mogoč: lokacija merilnega mesta še ni potrjena.';

/**
 * A station is a map object only when its location is known. With no located
 * station the SHP export is blocked; with one of two, the note names the one
 * the layer will not hold. The panel and the report page both read this.
 */
export function arsoShpNote(data: TimeseriesResponse): ArsoShpNote {
  if (data.station.out_of_pilot) return { blocked: true, note: ARSO_SHP_NO_LOCATION };
  const stations = [data.station, ...(data.compare ? [data.compare.station] : [])];
  const missing = stations.filter((s) => s.has_location === false);
  if (missing.length === 0) return { blocked: false, note: '' };
  if (missing.length === stations.length) return { blocked: true, note: ARSO_SHP_NO_LOCATION };
  return {
    blocked: false,
    note: `Izvoz SHP: merilno mesto »${missing.map((s) => s.name).join('«, »')}« ne bo v sloju, ker njegova lokacija še ni potrjena.`,
  };
}

export interface ArsoResult {
  html: string;
  chartEmpty: boolean;
  outOfPilot: boolean;
  /** What the chart draws (the caller passes it to buildArsoChartOption). */
  series: ArsoChartSeries[];
}

/**
 * Build the result body (everything under the banner). `actionsHtml` (export
 * row) is shown only when there is a chart + values — mirroring the panel, where
 * export is offered "ko je prikazan graf in vrednosti" (#363). Out-of-pilot
 * renders the static notice and no actions.
 */
export function buildArsoResult(
  data: TimeseriesResponse,
  opts: { interval: ArsoInterval; actionsHtml?: string },
): ArsoResult {
  const { interval, actionsHtml = '' } = opts;

  if (data.station.out_of_pilot) {
    return {
      html: `
        <div class="ngarso-block ngarso-out-of-pilot">
          <h4>${escapeHtml(ARSO_COPY.outOfPilotHintTitle)}</h4>
          <p>${escapeHtml(ARSO_COPY.outOfPilotHintBody)}</p>
        </div>
      `,
      chartEmpty: true,
      outOfPilot: true,
      series: [],
    };
  }

  const series = arsoDisplaySeries(data);
  const chartEmpty = !series.some((s) => s.series.length > 0);
  const exportRow = chartEmpty ? '' : actionsHtml;
  // Two stations: the legend names every line, but in a narrow panel it is
  // paged — this key says at a glance which line style is which station.
  const compareKey =
    data.compare && !chartEmpty
      ? `<p class="ngarso-compare-key" data-role="compare-key">` +
        `<span><span class="ngarso-key-line" aria-hidden="true"></span>${ARSO_COPY.compareKeySolid}: ${escapeHtml(data.station.name)}</span>` +
        `<span><span class="ngarso-key-line is-dashed" aria-hidden="true"></span>${ARSO_COPY.compareKeyDashed}: ${escapeHtml(data.compare.station.name)}</span>` +
        `</p>`
      : '';

  const html = `
    ${exportRow}
    <div class="ngarso-block">
      <h4>${ARSO_COPY.chartTitle}</h4>
      ${chartEmpty
        ? `<div class="ngarso-chart-empty">${escapeHtml(ARSO_COPY.chartEmpty)}</div>`
        : `<div class="ngarso-chart" data-role="chart"></div>${compareKey}`}
    </div>
    <div class="ngarso-block">
      <h4>${ARSO_COPY.tableTitle}</h4>
      ${buildArsoTableHtml(series, interval)}
    </div>
  `;

  return { html, chartEmpty, outOfPilot: false, series };
}

export function buildArsoTableHtml(seriesList: ArsoSeries[], interval: ArsoInterval): string {
  const visible = seriesList.filter((s) => s.series.length > 0);
  if (visible.length === 0) {
    return `<div class="ngarso-chart-empty">${escapeHtml(ARSO_COPY.empty)}</div>`;
  }

  // Build a wide table: rows = unique ts; columns = each series.
  const tsSet = new Set<string>();
  const byTs = visible.map((s) => {
    const m = new Map<string, number | null>();
    for (const p of s.series) {
      m.set(p.ts, p.value);
      tsSet.add(p.ts);
    }
    return m;
  });
  const tsList = [...tsSet].sort();

  const colHeaders = visible
    .map((s) => `<th class="ngarso-num">${escapeHtml(arsoSeriesLabel(s))} (${escapeHtml(s.unit)})</th>`)
    .join('');
  const rows = tsList
    .map((ts) => {
      const cells = visible
        .map((s, i) => {
          const v = byTs[i]?.get(ts);
          return `<td class="ngarso-num">${v == null ? '—' : formatValue(v, s.parameter)}</td>`;
        })
        .join('');
      return `<tr><td>${escapeHtml(formatTs(ts, interval))}</td>${cells}</tr>`;
    })
    .join('');

  return `
    <div class="ngarso-table-wrap">
      <table class="ngarso-table">
        <thead><tr><th>${ARSO_COPY.tableTime}</th>${colHeaders}</tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  `;
}

export function buildArsoChartOption(seriesList: ArsoChartSeries[], interval: ArsoInterval): EChartsOption {
  const visible = seriesList.filter((s) => s.series.length > 0);
  const colors = ['#2563eb', '#dc2626', '#059669', '#d97706', '#7c3aed', '#0891b2', '#db2777'];

  // Group units → axis index. Each unique unit gets its own y-axis (max 2
  // axes visible — extras share the right side stacked, but for the
  // pilot's typical 2–3 parameter selection this is fine).
  const unitIndex = new Map<string, number>();
  for (const s of visible) {
    if (!unitIndex.has(s.unit)) unitIndex.set(s.unit, unitIndex.size);
  }
  const yAxis = [...unitIndex.entries()].map(([unit, idx]) => ({
    type: 'value' as const,
    name: unit,
    position: idx === 0 ? ('left' as const) : ('right' as const),
    offset: idx > 1 ? (idx - 1) * 50 : 0,
    axisLabel: { fontSize: 10 },
    nameTextStyle: { fontSize: 10 },
  }));

  // Colour = the parameter, line style = the station (#720): the two stations'
  // lines of one parameter share a colour, the second station's is dashed.
  const colorIndex = new Map<ArsoSeriesParameter, number>();
  for (const s of visible) {
    if (!colorIndex.has(s.parameter)) colorIndex.set(s.parameter, colorIndex.size);
  }

  const series = visible.map((s) => {
    const color = colors[(colorIndex.get(s.parameter) ?? 0) % colors.length];
    return {
      name: arsoSeriesLabel(s),
      type: 'line' as const,
      yAxisIndex: unitIndex.get(s.unit) ?? 0,
      showSymbol: false,
      lineStyle: { width: 1.5, color, type: s.second ? ('dashed' as const) : ('solid' as const) },
      itemStyle: { color },
      data: s.series.map((p) => [p.ts, p.value]),
    };
  });

  const granularity = interval === 'daily' ? 'date' : 'datetime';
  return {
    tooltip: {
      trigger: 'axis',
      // Names carry the station in a comparison: the box stays inside the
      // chart and its lines wrap (a narrow panel would cut it off otherwise).
      confine: true,
      extraCssText: 'max-width: min(320px, 92%); white-space: normal;',
      axisPointer: { type: 'cross', label: { formatter: ljubljanaAxisPointerLabel() } },
      formatter: ljubljanaTooltipFormatter(granularity, (v) => (v == null ? '—' : v.toFixed(1))),
    },
    // The legend scrolls instead of wrapping over the plot; a long name is cut
    // and given in full in the legend's tooltip (the table has it too).
    legend: {
      type: 'scroll',
      textStyle: { fontSize: 11 },
      top: 0,
      formatter: legendName,
      tooltip: { show: true, formatter: (p: { name?: string }) => escapeHtml(p.name ?? '') },
    },
    // top: the axis names (units) sit under the legend row, not behind it.
    // bottom: room for the zoom slider under the time axis.
    grid: { left: 40, right: yAxis.length > 1 ? 40 + (yAxis.length - 1) * 50 : 12, top: 46, bottom: 58 },
    xAxis: {
      type: 'time',
      axisLabel: { fontSize: 10, hideOverlap: true, formatter: ljubljanaAxisLabel(granularity) },
    },
    yAxis,
    // Zoom and pan on the time axis (Priloga 2 §3.4): wheel / pinch / drag
    // inside the plot, and a slider under it. filterMode 'none' keeps a line
    // that crosses the window's edge drawn to the edge.
    dataZoom: [
      { type: 'inside', xAxisIndex: 0, filterMode: 'none' },
      {
        type: 'slider',
        xAxisIndex: 0,
        filterMode: 'none',
        height: 18,
        bottom: 8,
        labelFormatter: (value: number) => formatLjubljanaTs(new Date(value).toISOString(), 'date'),
      },
    ],
    series,
  } as EChartsOption;
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------
function formatTs(ts: string, interval: ArsoInterval): string {
  // Europe/Ljubljana, independent of the viewer's machine timezone.
  return formatLjubljanaTs(ts, interval === 'daily' ? 'date' : 'datetime');
}

function formatValue(v: number, parameter: ArsoSeriesParameter): string {
  // wind_dir + RH + pressure look better as integers (1015 hPa, not 1015.3);
  // promet_motor is a vehicle count (whole number); pollutant + temp keep 1 decimal.
  if (
    parameter === 'wind_dir' ||
    parameter === 'rh' ||
    parameter === 'pressure' ||
    parameter === 'promet_motor'
  ) {
    return v.toFixed(0);
  }
  return v.toFixed(1);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
